'use client';

import { useEffect, useRef, useState } from 'react';

import { HOMEPAGE_SIDENAV_SCROLL_OFFSET_PX } from './constants';
import {
  clearSectionHash,
  getActiveTag,
  getHashSection,
  getHomepageSideNavSectionId,
  normalizeHash,
  replaceSectionHash,
  scrollToHomepageSideNavSection,
} from './utils';
import { SideNavSection } from '../../types';

export function useActiveHomepageSideNavSection(sections: SideNavSection[]) {
  const [activeTag, setActiveTag] = useState(getActiveTag(sections));

  const lastHashRef = useRef(
    typeof window !== 'undefined' ? normalizeHash(window.location.hash) : ''
  );

  useEffect(() => {
    if (!sections.length) return;

    const sectionElements = sections
      .map((section) => {
        const element = document.getElementById(
          getHomepageSideNavSectionId(section)
        );

        return element ? { section, element } : null;
      })
      .filter(Boolean) as { section: SideNavSection; element: HTMLElement }[];

    if (!sectionElements.length) return;

    const hashSection = getHashSection(sections);
    if (hashSection) {
      lastHashRef.current = getHomepageSideNavSectionId(hashSection);
      requestAnimationFrame(() => scrollToHomepageSideNavSection(hashSection));
    }

    const syncActiveSection = () => {
      const offset = HOMEPAGE_SIDENAV_SCROLL_OFFSET_PX;
      let nextSection: SideNavSection | null = null;

      for (const { section, element } of sectionElements) {
        if (element.getBoundingClientRect().top <= offset + 8) {
          nextSection = section;
        }
      }

      if (!nextSection) {
        setActiveTag(sections[0]?.tag ?? '');
        clearSectionHash(lastHashRef);
        return;
      }

      setActiveTag(nextSection.tag);
      replaceSectionHash(nextSection, lastHashRef);
    };

    if (!hashSection) syncActiveSection();

    window.addEventListener('scroll', syncActiveSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', syncActiveSection);
    };
  }, [sections]);

  const selectSection = (section: SideNavSection) => {
    setActiveTag(section.tag);
    replaceSectionHash(section, lastHashRef);
    scrollToHomepageSideNavSection(section);
  };

  return { activeTag, selectSection };
}
